import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { showBanner } from '../../global-state/banner-slice'
import { kycServices } from '../../services'
import { RootState } from '../../store'
import { KYCPresenter } from './kyc-presenter'
import { updateConfiguration } from '../../global-state/investor-slice'
import { logCrmEvent, EngagementEvents } from '../../lib/utils/crmEvents'
import { consumeComplianceReturnPath } from '../../lib/utils/compliance-return-redirect'

const MAX_RETRY = 3

const KYCContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const { profileTab } = params
  const { authToken, accountStatus, userId, kycStatus } = useSelector(
    (state: RootState) => state.investor,
  )
  const [kycStatusData, setKycStatusData] = useState<string>(kycStatus || 'PENDING')
  const [noOfRejectedKyc, setNoOfRejectedKyc] = useState<number>(0)
  const [showContactUsModal, setShowContactUsModal] = useState(false)
  // REMOVED: showSelfDeclarationSigning state - now clubbed with client agreement
  const [selfDeclarationSigned, setSelfDeclarationSigned] = useState(false)
  const [showClientAgreementSigning, setShowClientAgreementSigning] = useState(false)
  const [clientAgreementSigned, setClientAgreementSigned] = useState(false)
  const [portfolioIds, setPortfolioIds] = useState<
    Array<{ investor_portfolio_id: string; status: string; created_at: string }>
  >([])

  const handleClickModal = () => {
    setShowContactUsModal((state) => !state)
  }

  // REMOVED: showSelfDeclarationSigningComponent and hideSelfDeclarationSigningComponent - now clubbed with client agreement

  // REMOVED: handleSelfDeclarationSigned - now handled via client agreement

  const navigateToComplianceReturnIfPresent = (): boolean => {
    const returnPath = consumeComplianceReturnPath()
    if (!returnPath) return false

    navigate(returnPath)
    return true
  }

  const showClientAgreementSigningComponent = () => {
    document.body.style.overflow = 'hidden'
    setShowClientAgreementSigning(true)
  }

  const hideClientAgreementSigningComponent = () => {
    document.body.style.overflow = 'auto'
    setShowClientAgreementSigning(false)

    logCrmEvent(
      EngagementEvents.CLIENT_AGREEMENT_SIGNED,
      {
        Authorization: authToken ? authToken : '',
      },
      {
        userId: userId,
      },
    )
  }

  const handleClientAgreementSigned = async () => {
    try {
      // Since client agreement now includes self-declaration, set both as signed
      setSelfDeclarationSigned(true)
      setClientAgreementSigned(true)
      hideClientAgreementSigningComponent()
      // Stay on KYC if verification is still pending; only return after both are complete.
      if (kycStatusData === 'APPROVED' && navigateToComplianceReturnIfPresent()) {
        return
      }
      getKYC()
    } catch (error) {
      console.error('Error handling client agreement signing:', error)
    }
  }

  const getKYC = async () => {
    try {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (response?.data) {
        setKycStatusData(response.data.status ? response.data.status : '')
        setNoOfRejectedKyc(response.data.noOfRejectedKyc)

        const kycSelfDeclarationSigned = !!response.data.self_declaration
        const kycClientAgreementSigned = !!response.data.client_agreement_signed

        // Both self-declaration and client agreement are now handled by single client agreement document
        setSelfDeclarationSigned(kycSelfDeclarationSigned)
        setClientAgreementSigned(kycClientAgreementSigned)

        // Client agreement can be signed before KYC; show it whenever unsigned.
        if (!kycClientAgreementSigned) {
          showClientAgreementSigningComponent()
        }

        if (response.data.status) {
          dispatch(
            updateConfiguration({
              name: 'kycStatus',
              value: response.data.status,
            }),
          )
          if (response.data.status !== kycStatus && response.data.status === 'APPROVED')
            dispatch(showBanner({ text: 'KYC Approved', variant: 'success-banner' }))
        }

        // Only return to the originating opportunity once both compliance steps are done.
        if (response.data.status === 'APPROVED' && kycClientAgreementSigned) {
          navigateToComplianceReturnIfPresent()
        }
      }
    } catch (error) {
      console.error('Error getting KYC:', error)
    }
  }

  const getInvestorPortfolioIds = async () => {
    try {
      const response = await kycServices.getInvestorPortfolioIds(userId, {
        Authorization: authToken ? authToken : '',
      })
      if (response?.data && response?.status === 200) {
        const portfolioIds = response.data?.map(
          (item: { investor_portfolio_id: string; status: string; created_at: string }) => ({
            investor_portfolio_id: item.investor_portfolio_id,
            status: item.status,
            created_at: item.created_at,
          }),
        )

        setPortfolioIds([...portfolioIds])
      }
    } catch (error) {
      console.error('Error getting portfolio IDs:', error)
    }
  }

  useEffect(() => {
    getKYC()
    getInvestorPortfolioIds()
  }, [])

  // REMOVED: useEffect for self-declaration signing check - now handled via client agreement

  useEffect(() => {
    if (clientAgreementSigned && showClientAgreementSigning) {
      hideClientAgreementSigningComponent()
    }
  }, [clientAgreementSigned, showClientAgreementSigning])

  return (
    <KYCPresenter
      getKYC={getKYC}
      handleClickModal={handleClickModal}
      handleSelfDeclarationSigned={handleClientAgreementSigned} // Now handled via client agreement
      // REMOVED: hideSelfDeclarationSigningComponent - now clubbed with client agreement
      handleClientAgreementSigned={handleClientAgreementSigned}
      hideClientAgreementSigningComponent={hideClientAgreementSigningComponent}
      kycStatusData={kycStatusData}
      noOfRejectedKyc={noOfRejectedKyc}
      // REMOVED: showSelfDeclarationSigning - now clubbed with client agreement
      showClientAgreementSigning={showClientAgreementSigning}
      profileTab={profileTab}
      accountStatus={accountStatus}
      MAX_RETRY={MAX_RETRY}
      showContactUsModal={showContactUsModal}
      portfolioIds={portfolioIds}
      selfDeclarationSigned={selfDeclarationSigned}
      clientAgreementSigned={clientAgreementSigned}
    />
  )
}

export { KYCContainer }
